/******/ (() => { // webpackBootstrap
const SEARCH_RETURN_KEY = 'miss-daisy-predictive-search-return';
const SEARCH_RETURN_MAX_AGE = 30 * 60 * 1000;

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    // Children may not exist yet when this element is upgraded or cloned.
    this.searchQuery = '';
    this.searchChangeTimer = null;
    this.searchAbortController = null;
    this.initialized = false;

    // Bind event handlers
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleInputKeyDown = this.handleInputKeyDown.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleClearButtonVisibility = this.handleClearButtonVisibility.bind(this);
    this.handleNavButtonClick = this.handleNavButtonClick.bind(this);
    this.handleRecoveryActionClick = this.handleRecoveryActionClick.bind(this);
    this.handleResultNavigation = this.handleResultNavigation.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
    this.handleSearchExit = this.handleSearchExit.bind(this);
    this.handleSearchExitKeyDown = this.handleSearchExitKeyDown.bind(this);
    this.resetState = this.resetState.bind(this);
    this.handleSharedQuery = this.handleSharedQuery.bind(this);
    this.activateSearch = this.activateSearch.bind(this);
    this.handleMenuOpen = this.handleMenuOpen.bind(this);
    this.handleLayoutChange = this.handleLayoutChange.bind(this);
    this.handleSuggestionKeyDown = this.handleSuggestionKeyDown.bind(this);
  }

  cacheDOMElements() {
    this.input = this.querySelector('[data-predictive-search-input]');
    this.form = this.querySelector('[data-predictive-search-form]');
    this.submitButton = this.querySelector('[data-predictive-search-submit]');
    this.clearButton = this.querySelector('[data-predictive-search-clear]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.emptyStateContainer = this.querySelector('[data-predictive-search-empty-state]');
    this.boxShadowElement = this.closest('.popup-shadow');

    this.isPredictive = this.getAttribute('is-predictive') === 'true';
    this.resourceTypes = this.getAttribute('resource-types') || 'query,product,collection,page,article';
  }

  connectedCallback() {
    if (!this.isConnected || this.initialized) return;
    this.cacheDOMElements();
    if (!this.input || !this.clearButton || (!this.form && !this.submitButton)) {
      // A parser or section update can connect the host before its children.
      if (!this.initializationObserver) {
        this.initializationObserver = new MutationObserver(() => this.connectedCallback());
        this.initializationObserver.observe(this, { childList: true, subtree: true });
      }
      return;
    }
    this.initializationObserver?.disconnect();
    this.initializationObserver = null;
    this.initialized = true;
    this.searchQuery = this.input.value;
    window.wetheme.webcomponentRegistry.register({key: 'component-predictive-search'});

    // Add event listeners
    this.input.addEventListener('input', this.handleInputChange);
    this.input.addEventListener('keydown', this.handleInputKeyDown);
    this.input.addEventListener('focus', this.activateSearch);
    if (this.form) this.form.addEventListener('submit', this.handleSubmit);
    else this.submitButton.addEventListener('click', this.handleSubmit);
    this.clearButton.addEventListener('click', this.handleClear);
    this.resultsContainer?.addEventListener('click', this.handleRecoveryActionClick);
    this.resultsContainer?.addEventListener('click', this.handleResultNavigation);
    this.resultsContainer?.addEventListener('keydown', this.handleSuggestionKeyDown);
    this.searchLayout = window.matchMedia?.('(min-width: 1025px)');
    this.searchLayout?.addEventListener('change', this.handleLayoutChange);
    window.addEventListener('pageshow', this.handlePageShow);
    document.addEventListener('click', this.handleSearchExit, true);
    document.addEventListener('keydown', this.handleSearchExitKeyDown, true);
    document.addEventListener('mdh:search-query', this.handleSharedQuery);
    document.addEventListener('click', this.handleMenuOpen);
    document.documentElement.addEventListener('wetheme-toggle-left-drawer', this.handleMenuOpen);
    this.handleClearButtonVisibility();

    // Safe-load-scripts can define this element after pageshow has fired.
    if (document.readyState === 'complete') this.restoreSearchContext();
  }

  handleInputChange() {
    this.handleClearButtonVisibility();
    this.publishQuery();

    if (!this.isPredictive) return;

    if (this.input.value !== '') {
      this.searchQuery = this.input.value;

      if (this.searchAbortController) {
        this.searchAbortController.abort();
        this.searchAbortController = null;
      }

      this.resultsContainer?.setAttribute('aria-busy', 'true');
      this.showLoadingState();

      if (this.emptyStateContainer) {
        this.emptyStateContainer.setAttribute('hidden', '');
      }

      if (this.searchChangeTimer) {
        window.clearTimeout(this.searchChangeTimer);
      }

      this.searchChangeTimer = window.setTimeout(() => {
        this.performSearch(this.searchQuery);
        this.searchChangeTimer = null;
      }, 250);
    }
    else {
      this.resetState();
    }
  }

  handleClearButtonVisibility() {
    // Synchronous state avoids an old hide timer concealing X after retyping.
    const empty = this.input.value === '';
    this.clearButton.hidden = empty;
    this.clearButton.disabled = empty;
    this.clearButton.classList.remove('hide', 'fade-out');
  }

  publishQuery() {
    document.dispatchEvent(new CustomEvent('mdh:search-query', {
      detail: { source: this, query: this.input.value },
    }));
  }

  handleSharedQuery(event) {
    if (event.detail.source === this || event.detail.query === this.input.value) return;
    this.input.value = event.detail.query;
    this.searchQuery = event.detail.query;
    this.resetState();
    this.handleClearButtonVisibility();
    // The hidden surface must not make duplicate search requests. It refreshes
    // only when opened or focused, using the same query and product endpoint.
  }

  activateSearch() {
    const query = this.input.value.trim();
    if (!this.isPredictive || !query) return;
    if (this.lastRenderedQuery === query && ['success', 'zero'].includes(this.dataset.searchState)) return;
    if (this.dataset.searchState === 'loading') return;
    this.handleInputChange();
  }

  handleMenuOpen(event) {
    if (this.getSearchSurface() !== 'mobile-menu') return;
    if (event.type === 'click' && !event.target.closest('.js-drawer-open-left-link')) return;
    queueMicrotask(() => {
      if (this.isConnected && document.body.classList.contains('js-drawer-open-left')) this.activateSearch();
    });
  }

  handleInputKeyDown(event) {
    if (event.isComposing) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const links = this.getSuggestionLinks();
      if (links.length) {
        event.preventDefault();
        links[event.key === 'ArrowDown' ? 0 : links.length - 1].focus();
      }
      return;
    }
    if (event.key === 'Enter' && !event.isComposing && !this.form) {
      event.preventDefault();
      this.handleSubmit(event);
    }
  }

  handleSubmit(event) {
    event?.preventDefault();
    const query = this.input.value.trim();

    if (query === '') {
      this.input.focus({ preventScroll: true });
      return;
    }

    this.clearReturnContext();

    const searchParams = new URLSearchParams({
      q: query,
      type: 'product',
    });
    searchParams.set('options[prefix]', 'last');

    window.location.href = `${window.Shopify.routes.root}search?${searchParams.toString()}`;
  }

  handleClear(event) {
    event?.preventDefault();
    event?.stopPropagation();
    this.clearReturnContext();
    this.input.value = '';
    this.searchQuery = '';
    this.handleClearButtonVisibility();
    this.resetState();
    this.publishQuery();
    this.input.focus({ preventScroll: true });
  }

  async performSearch(query) {
    const normalizedQuery = query.trim();
    if (normalizedQuery === '') {
      this.resetState();
      return;
    }

    if (this.searchAbortController) {
      this.searchAbortController.abort();
    }

    const searchAbortController = new AbortController();
    this.searchAbortController = searchAbortController;

    const desktop = this.isDesktopSearch();

    try {
      // Mobile requests keywords only. Desktop retains storefront-ranked product
      // previews, independently of Shopify's keyword suggestion service.
      const [keywords, products] = await Promise.allSettled([
        this.fetchSuggestions(normalizedQuery, searchAbortController.signal),
        desktop ? this.fetchProductPreview(normalizedQuery, searchAbortController.signal) : Promise.resolve(null),
      ]);
      if (this.searchAbortController !== searchAbortController || this.input.value.trim() !== normalizedQuery) return;
      const suggestions = keywords.status === 'fulfilled' ? keywords.value : [];
      const preview = products.status === 'fulfilled' ? products.value : null;
      this.emptyStateContainer?.setAttribute('hidden', '');
      this.renderAssistance({
        query: normalizedQuery, suggestions, desktop, preview,
        keywordsFailed: keywords.status === 'rejected',
        productsFailed: products.status === 'rejected',
      });
      this.dataset.searchState = suggestions.length || (preview && !preview.zero)
        ? 'success' : keywords.status === 'rejected' ? 'error' : 'zero';
      this.lastRenderedQuery = normalizedQuery;
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error(`Failed to fetch search results:`, error);
      if (this.searchAbortController === searchAbortController && this.input.value.trim() === normalizedQuery) {
        this.renderSearchError();
      }
    } finally {
      if (this.searchAbortController === searchAbortController) {
        this.searchAbortController = null;
        this.resultsContainer?.removeAttribute('aria-busy');
      }
    }
  }

  isDesktopSearch() {
    return this.searchLayout?.matches ?? false;
  }

  handleLayoutChange() {
    const restoreFocus = this.resultsContainer?.contains(document.activeElement);
    this.resetState();
    const dropdown = this.closest('search-dropdown');
    const open = dropdown ? dropdown.getAttribute('aria-hidden') === 'false'
      : !this.isDesktopSearch() && document.body.classList.contains('js-drawer-open-left');
    if (open) {
      if (restoreFocus) this.input.focus({ preventScroll: true });
      this.activateSearch();
    }
  }

  getSuggestionLinks() {
    return Array.from(this.resultsContainer?.querySelectorAll('[data-search-suggestion]') || []);
  }

  handleSuggestionKeyDown(event) {
    if (event.isComposing || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    const links = this.getSuggestionLinks();
    const index = links.indexOf(event.target.closest('[data-search-suggestion]'));
    if (index < 0) return;
    event.preventDefault();
    const next = index + (event.key === 'ArrowDown' ? 1 : -1);
    if (next < 0 || next >= links.length) this.input.focus({ preventScroll: true });
    else links[next].focus();
  }

  normalizeSuggestions(queries) {
    if (!Array.isArray(queries)) return [];
    const seen = new Set();
    return queries.filter(item => {
      if (typeof item?.text !== 'string' || !item.text.trim()) return false;
      const key = item.text.trim().toLocaleLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 6).map(item => ({ text: item.text.trim() }));
  }

  async fetchSuggestions(query, signal) {
    const params = new URLSearchParams({ q: query, 'resources[type]': 'query', 'resources[limit]': '6' });
    const response = await fetch(`${window.Shopify.routes.root}search/suggest.json?${params}`, { signal });
    if (!response.ok) throw new Error(`Suggestions failed with status ${response.status}`);
    const payload = await response.json();
    if (!Array.isArray(payload?.resources?.results?.queries)) throw new Error('Invalid suggestions response');
    return this.normalizeSuggestions(payload.resources.results.queries);
  }

  async fetchProductPreview(query, signal) {
    const params = new URLSearchParams({ q: query, type: 'product', section_id: 'search-preview-results', 'options[prefix]': 'last' });
    const response = await fetch(`${window.Shopify.routes.root}search?${params}`, { signal });
    if (!response.ok) throw new Error(`Product preview failed with status ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const content = doc.querySelector('.shopify-section');
    if (!content) throw new Error('Invalid product preview response');
    return { html: content.innerHTML, zero: Boolean(content.querySelector('.search-recovery')) };
  }

  createSuggestionLink(text, query, fallback = false) {
    const link = document.createElement('a');
    const params = new URLSearchParams({ q: text, type: 'product', 'options[prefix]': 'last' });
    link.href = `${window.Shopify.routes.root}search?${params}`;
    link.className = 'search-suggestions__link';
    link.setAttribute('data-search-suggestion', '');
    // This fixed icon is the only HTML here. Query/API text is always textContent.
    link.innerHTML = '<svg aria-hidden="true" viewBox="0 0 20 20" fill="none" width="20" height="20"><circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor"/><path d="m13 13 4 4" stroke="currentColor"/></svg>';
    const label = document.createElement('span');
    if (fallback) label.textContent = `Search for “${text}”`;
    else {
      const start = text.toLocaleLowerCase().indexOf(query.toLocaleLowerCase());
      if (start < 0) label.textContent = text;
      else {
        const match = document.createElement('strong');
        match.textContent = text.slice(start, start + query.length);
        label.append(document.createTextNode(text.slice(0, start)), match, document.createTextNode(text.slice(start + query.length)));
      }
    }
    link.append(label);
    return link;
  }

  renderAssistance({ query, suggestions, desktop, preview, keywordsFailed, productsFailed }) {
    this.renderSearchResults('');
    const layout = document.createElement('div');
    layout.className = 'search-assist';
    const keywords = document.createElement('nav');
    keywords.className = 'search-suggestions';
    keywords.setAttribute('aria-label', 'Suggested searches');
    const heading = document.createElement('p');
    heading.className = 'search-suggestions__heading h5';
    heading.textContent = 'Suggested searches';
    keywords.append(heading);
    if (keywordsFailed) {
      const message = document.createElement('p');
      message.className = 'search-suggestions__message';
      message.textContent = 'Suggestions are temporarily unavailable. You can still search.';
      keywords.append(message);
    }
    const list = document.createElement('ul');
    for (const item of suggestions.length ? suggestions : [{ text: query }]) {
      const row = document.createElement('li');
      row.append(this.createSuggestionLink(item.text, query, !suggestions.length));
      list.append(row);
    }
    keywords.append(list);
    layout.append(keywords);
    if (desktop) {
      const products = document.createElement('div');
      products.className = 'search-assist__products';
      if (preview) products.innerHTML = preview.html;
      else if (productsFailed) {
        const message = document.createElement('p');
        message.className = 'search-suggestions__message';
        message.textContent = 'Product previews are temporarily unavailable.';
        products.append(message, this.createSuggestionLink(query, query, true));
      }
      layout.append(products);
    }
    this.resultsContainer.append(layout);
  }

  renderSearchError() {
    this.renderSearchResults('');
    this.dataset.searchState = 'error';

    const status = document.createElement('p');
    status.className = 'predictive-search__status';
    status.setAttribute('role', 'status');
    status.textContent = this.dataset.searchErrorMessage || 'Search is temporarily unavailable. Please try again.';
    this.resultsContainer.append(status);
  }

  showLoadingState() {
    this.dataset.searchState = 'loading';
    this.renderSearchResults('');

    const status = document.createElement('p');
    status.className = 'predictive-search__status predictive-search__status--loading';
    status.setAttribute('role', 'status');
    status.textContent = this.dataset.searchLoadingMessage || 'Searching…';
    this.resultsContainer?.append(status);
  }

  renderSearchResults(html) {
    if (!this.resultsContainer) return;
    this.resultsContainer.innerHTML = html;

    // Remove any previously tracked event listeners
    if (this.boundNavButtons) {
      this.boundNavButtons.forEach(el => {
        el.removeEventListener('click', this.handleNavButtonClick);
      });
    }

    // Track elements with event listeners for later cleanup
    this.boundNavButtons = [];

    // Add nav button event listeners
    const navButtons = this.resultsContainer.querySelector('[data-predictive-search-nav-buttons]');
    if (navButtons) {
      navButtons.addEventListener('click', this.handleNavButtonClick);
      this.boundNavButtons.push(navButtons);
    }
  }

  handleNavButtonClick(e) {
    e.stopPropagation();
    const navButton = e.target;
    const resultId = navButton.getAttribute('data-nav-id');
    if (!resultId) return;

    const results = this.resultsContainer.querySelector(`[data-grid-id="${resultId}"]`);
    if (!results) return;

    const previousActiveButton = this.resultsContainer.querySelector(`[data-nav-id].active`);
    const previousActiveResult = this.resultsContainer.querySelector(`[data-grid-id]:not([hidden])`);

    // Set active nav button
    if (previousActiveButton) {
      previousActiveButton.setAttribute('aria-selected', 'false');
      previousActiveButton.classList.remove('active');
    }
    navButton.setAttribute('aria-selected', 'true');
    navButton.classList.add('active');

    // Set active results
    const heightContainer = this.resultsContainer.querySelector('[data-predictive-search-main-results]');
    const beforeHeight = heightContainer.clientHeight;

    if (previousActiveResult) {
      previousActiveResult.setAttribute('hidden', '');
    }
  
    results.removeAttribute('hidden');

    // Transition the height change
    heightContainer.animate([
      { height: `${beforeHeight}px` },
      { height: `${heightContainer.clientHeight}px` }
    ], { duration: 500, easing: 'cubic-bezier(0.85, 0, 0.15, 1)' });
  }

  handleRecoveryActionClick(event) {
    const clearButton = event.target.closest('[data-search-recovery-clear]');
    if (!clearButton) return;

    event.preventDefault();
    // The button is removed with its results. Stop this same click reaching
    // the panel's outside-click handler with a now-detached target.
    this.handleClear(event);
  }

  getSearchSurface() {
    return this.closest('#NavDrawer') ? 'mobile-menu' : 'dropdown';
  }

  getReturnSurface(context) {
    // A shopper may rotate the device or resize the window on the product page.
    return context.surface === 'mobile-menu' && window.matchMedia('(max-width: 1024px)').matches
      ? 'mobile-menu'
      : 'dropdown';
  }

  getReturnContext() {
    try {
      const context = JSON.parse(sessionStorage.getItem(SEARCH_RETURN_KEY));
      if (!context) return null;
      if (Date.now() - context.savedAt > SEARCH_RETURN_MAX_AGE) {
        sessionStorage.removeItem(SEARCH_RETURN_KEY);
        return null;
      }
      return context;
    } catch (_) {
      return null;
    }
  }

  clearReturnContext() {
    const context = this.getReturnContext();
    if (context?.source === window.location.href.split('#')[0]) {
      try {
        sessionStorage.removeItem(SEARCH_RETURN_KEY);
      } catch (_) {
        // Search must still work when session storage is unavailable.
      }
    }
  }

  handleResultNavigation(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest('a[href]');
    if (!link || (link.target && link.target !== '_self')) return;

    const destination = new URL(link.href, window.location.href);
    const query = this.input.value.trim();
    if (!query || destination.origin !== window.location.origin) return;
    if (!destination.pathname.includes('/products/') && !link.hasAttribute('data-search-suggestion')) return;

    try {
      sessionStorage.setItem(SEARCH_RETURN_KEY, JSON.stringify({
        source: window.location.href.split('#')[0],
        query,
        surface: this.getSearchSurface(),
        savedAt: Date.now(),
      }));
    } catch (_) {
      // The product link remains usable when session storage is unavailable.
    }
  }

  handlePageShow(event) {
    this.restoreSearchContext(event.persisted);
  }

  async restoreSearchContext(wasRestoredFromCache = false) {
    if (this.restoringSearch || !this.isPredictive) return;

    const navigation = performance.getEntriesByType('navigation')[0];
    if (!wasRestoredFromCache && navigation?.type !== 'back_forward') {
      this.clearReturnContext();
      return;
    }

    const context = this.getReturnContext();
    if (!context || context.source !== window.location.href.split('#')[0] || this.getReturnSurface(context) !== this.getSearchSurface()) return;

    this.restoringSearch = true;
    try {
      if (this.getSearchSurface() === 'mobile-menu') {
        document.documentElement.dispatchEvent(new CustomEvent('wetheme-toggle-left-drawer', { detail: { forceOpen: true } }));
      } else {
        await customElements.whenDefined('search-dropdown');
        const dropdown = document.querySelector('search-dropdown');
        if (!dropdown) return;
        if (dropdown.getAttribute('aria-hidden') === 'true') dropdown.openDropdown();
      }

      if (!this.isConnected) return;
      this.input.value = context.query;
      this.handleInputChange();
      this.input.focus();
    } finally {
      this.restoringSearch = false;
    }
  }

  handleSearchExit(event) {
    const context = this.getReturnContext();
    if (!context || context.source !== window.location.href.split('#')[0] || this.getReturnSurface(context) !== this.getSearchSurface()) return;

    const target = event.target;
    const link = target.closest('a[href]');
    if (link && this.resultsContainer?.contains(link) && (link.hasAttribute('data-search-suggestion') || new URL(link.href, window.location.href).pathname.includes('/products/'))) return;

    const drawer = this.closest('search-dropdown, #NavDrawer');
    if (link || target.closest('[data-predictive-search-clear], [data-search-dropdown-close], .mobile-menu--close-btn') || (drawer && !drawer.contains(target))) {
      this.clearReturnContext();
    }
  }

  handleSearchExitKeyDown(event) {
    if (event.key === 'Escape') this.clearReturnContext();
  }

  resetState() {
    if (this.searchChangeTimer) {
      window.clearTimeout(this.searchChangeTimer);
      this.searchChangeTimer = null;
    }

    if (this.searchAbortController) {
      this.searchAbortController.abort();
      this.searchAbortController = null;
    }

    this.resultsContainer?.removeAttribute('aria-busy');
    this.dataset.searchState = 'idle';
    this.lastRenderedQuery = '';
    this.emptyStateContainer?.removeAttribute('hidden');
    this.renderSearchResults('');
  }

  disconnectedCallback() {
    this.initializationObserver?.disconnect();
    this.initializationObserver = null;
    if (!this.initialized) return;
    this.initialized = false;
    if (this.searchChangeTimer) {
      window.clearTimeout(this.searchChangeTimer);
      this.searchChangeTimer = null;
    }

    if (this.searchAbortController) {
      this.searchAbortController.abort();
      this.searchAbortController = null;
    }

    // Remove event listeners
    this.input.removeEventListener('input', this.handleInputChange);
    this.input.removeEventListener('keydown', this.handleInputKeyDown);
    this.input.removeEventListener('focus', this.activateSearch);
    if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
    else this.submitButton.removeEventListener('click', this.handleSubmit);
    this.clearButton.removeEventListener('click', this.handleClear);
    this.resultsContainer?.removeEventListener('click', this.handleRecoveryActionClick);
    this.resultsContainer?.removeEventListener('click', this.handleResultNavigation);
    this.resultsContainer?.removeEventListener('keydown', this.handleSuggestionKeyDown);
    this.searchLayout?.removeEventListener('change', this.handleLayoutChange);
    window.removeEventListener('pageshow', this.handlePageShow);
    document.removeEventListener('click', this.handleSearchExit, true);
    document.removeEventListener('keydown', this.handleSearchExitKeyDown, true);
    document.removeEventListener('mdh:search-query', this.handleSharedQuery);
    document.removeEventListener('click', this.handleMenuOpen);
    document.documentElement.removeEventListener('wetheme-toggle-left-drawer', this.handleMenuOpen);

    // Clean up dynamic listeners
    if (this.boundNavButtons) {
      this.boundNavButtons.forEach(el => {
        el.removeEventListener('click', this.handleNavButtonClick);
      });
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);

/******/ })()
;
